import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Send, AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  freelanceGigsApi,
  FreelanceGigMessage,
} from '../services/freelanceGigsApi';
import './FreelanceGigs.scss';

/**
 * Shared message thread page for a single (gig, response) pair.
 *
 * Used by BOTH the employer (viewing responders to their gig) and the
 * employee (viewing their own response). Backend access control gates
 * who can read/post; the UI behaves identically.
 *
 * Polling every 15s keeps the thread reasonably fresh without WS overhead.
 */
const POLL_INTERVAL_MS = 15_000;

const FreelanceMessages: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { respId } = useParams<{ respId: string }>();
  const navigate = useNavigate();

  const [messages, setMessages] = useState<FreelanceGigMessage[]>([]);
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const responseId = respId ? parseInt(respId, 10) : NaN;

  const refresh = useCallback(async () => {
    if (Number.isNaN(responseId)) return;
    try {
      const { messages } = await freelanceGigsApi.messages(responseId);
      setMessages(messages);
      setError(null);
      // The GET above marks the other side's messages as read on the server.
      // Notify the Layout so the nav badge updates immediately rather than
      // waiting for the next route change.
      window.dispatchEvent(new CustomEvent('freelance-unread-changed'));
    } catch (err: any) {
      setError(err?.message || 'Nepavyko užkrauti pokalbio');
    } finally {
      setLoading(false);
    }
  }, [responseId]);

  useEffect(() => {
    if (!isAuthenticated) return;
    refresh();
    const t = window.setInterval(refresh, POLL_INTERVAL_MS);
    return () => window.clearInterval(t);
  }, [isAuthenticated, refresh]);

  // Auto-scroll to the bottom on new messages.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  if (isLoading) return <div className="freelance-page-loading">Kraunama...</div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  const send = async () => {
    const trimmed = body.trim();
    if (!trimmed || sending) return;
    setSending(true);
    setError(null);
    try {
      await freelanceGigsApi.postMessage(responseId, trimmed);
      setBody('');
      await refresh();
    } catch (err: any) {
      setError(err?.message || 'Nepavyko išsiųsti žinutės');
    } finally {
      setSending(false);
    }
  };

  const onKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      send();
    }
  };

  const meId = user?.id;

  // Same fallback strategy as `InterviewThread`: history.back() silently
  // does nothing when the user opened this URL directly (email link, fresh
  // tab, hard reload), so we route to the role-appropriate list page.
  const handleBack = () => {
    if (user?.user_type === 'employer') {
      navigate('/freelance/gigs?mode=employer');
    } else if (user?.user_type === 'employee') {
      navigate('/freelance/my-responses');
    } else if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  return (
    <div className="freelance-thread-page">
      <button className="freelance-back-link" type="button" onClick={handleBack}>
        <ChevronLeft size={16} /> Atgal
      </button>

      <h1 className="freelance-thread-title">Žinutės</h1>

      {error && (
        <div className="freelance-alert freelance-alert--error">
          <AlertTriangle size={16} /> {error}
        </div>
      )}

      <div className="freelance-thread-window" ref={scrollRef}>
        {loading ? (
          <div className="freelance-page-loading">Kraunama...</div>
        ) : messages.length === 0 ? (
          <p className="freelance-empty-hint">Pradėkite pokalbį parašydami žinutę žemiau.</p>
        ) : (
          messages.map((m) => {
            const mine = m.from_user_id === meId;
            return (
              <div key={m.id} className={`freelance-msg ${mine ? 'freelance-msg--mine' : 'freelance-msg--theirs'}`}>
                <div className="freelance-msg-bubble">{m.body}</div>
                <div className="freelance-msg-time">
                  {new Date(m.created_at).toLocaleString('lt-LT')}
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="freelance-thread-composer">
        <textarea
          rows={3}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={onKey}
          placeholder="Parašykite žinutę... (Ctrl/⌘+Enter siųsti)"
          maxLength={5000}
        />
        <button
          type="button"
          className="btn btn-primary"
          disabled={sending || !body.trim()}
          onClick={send}
        >
          <Send size={14} /> Siųsti
        </button>
      </div>
    </div>
  );
};

export default FreelanceMessages;
